import { Table } from "@ptsd/uikit";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text, View } from "tamagui";

import type { RouterOutputs } from "~/utils/api";

type TData = RouterOutputs["person"]["invite"][number];
type Details = TData["details"][number];

const columnHelper = createColumnHelper<Details>();

export const InviteStatusTable: React.FC<{
  data: TData;
}> = ({ data }) => {
  const cols = data.columns.map((col) => {
    return columnHelper.accessor(col.key as keyof Details, {
      header: col.name,
      cell: (info) => {
        return info.getValue();
      },
    });
  });

  const table = useReactTable({
    data: data.details,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  if (data.details.length === 0) return null;

  return (
    <View gap={"$3"}>
      <Text
        fontFamily={"$body"}
        fontWeight={"$4"}
        fontSize={"$3"}
        lineHeight={"$3"}
        color={"$gray-800"}
        textAlign="center"
        data-id="invite-status-message"
      >
        {data?.message}
      </Text>

      <Table
        alignCells={{ x: "center", y: "center" }}
        alignHeaderCells={{ y: "center", x: "center" }}
        cellHeight="$5.8"
        data-id="invite-status-table"
      >
        <Table.Head data-id="invite-status-table-head">
          {headerGroups.map((headerGroup, index) => {
            return (
              <Table.Row
                rowLocation={
                  index === 0
                    ? "first"
                    : index === headerGroups.length - 1
                      ? "last"
                      : "middle"
                }
                key={headerGroup.id}
                justifyContent="flex-start"
              >
                {headerGroup.headers.map((header, index) => {
                  return (
                    <Table.HeaderCell
                      backgroundColor="white"
                      key={header.id}
                      justifyContent="flex-start"
                      borderWidth={0}
                      cellLocation={
                        index === 0
                          ? "first"
                          : index === headerGroup.headers.length - 1
                            ? "last"
                            : "middle"
                      }
                    >
                      <Text
                        fontFamily={"$subtitle"}
                        fontSize={"$2"}
                        lineHeight={"$2"}
                        fontWeight={"$5"}
                        color={"$gray-primary-900"}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Text>
                    </Table.HeaderCell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Head>

        <Table.Body>
          {tableRows.length === 0 ? (
            <Table.Row
              borderBottomRightRadius={"$1"}
              borderBottomLeftRadius={"$1"}
              backgroundColor={"white"}
              key={1}
            >
              <Table.Cell
                borderColor={"$gray-200"}
                width={"100%"}
                key={1}
                justifyContent="center"
              >
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  fontFamily={"$subtitle"}
                  fontSize={"$2"}
                  lineHeight={"$2"}
                  color={"$gray-primary-900"}
                >
                  Немає даних
                </Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            tableRows?.map((row, index) => {
              return (
                <Table.Row
                  backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                  rowLocation={
                    index === 0
                      ? "first"
                      : index === tableRows.length - 1
                        ? "last"
                        : "middle"
                  }
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <Table.Cell
                        justifyContent="flex-start"
                        cellLocation={
                          index === 0
                            ? "first"
                            : row.getVisibleCells().length - 1 === index
                              ? "last"
                              : "middle"
                        }
                        key={cell.id}
                      >
                        <Text
                          width={"100%"}
                          fontFamily={"$subtitle"}
                          lineHeight={"$2"}
                          fontSize={"$2"}
                          fontWeight={"$4"}
                          color={"$gray-primary-900"}
                          ellipsizeMode="tail"
                          numberOfLines={1}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Text>
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table>
    </View>
  );
};
